(() => {
  const rootSelector = '[data-main-article]';
  const contentSelector = '[data-main-article-content]';
  const tocSelector = '[data-main-article-toc]';
  const tocListSelector = '[data-main-article-toc-list]';
  const relatedListSelector = '[data-main-article-related-list]';
  const relatedControlSelector = '[data-article-related-control]';
  const relatedProgressSelector = '[data-article-related-progress]';
  const relatedProgressThumbSelector = '[data-article-related-progress-thumb]';

  const slugify = (value) =>
    value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const buildToc = (section) => {
    const content = section.querySelector(contentSelector);
    const toc = section.querySelector(tocSelector);
    const tocList = section.querySelector(tocListSelector);

    if (!content || !toc || !tocList) return;

    const tocExcludeSelector = [
      '.article-featured-products',
      '.main-article__newsletter',
      '.main-article__sources',
      '.main-article__related',
      '[data-article-toc-exclude]',
    ].join(',');

    const headings = Array.from(content.querySelectorAll('h2, h3')).filter(
      (heading) => heading.textContent.trim() && !heading.closest(tocExcludeSelector)
    );

    if (headings.length === 0) {
      toc.hidden = true;
      return;
    }

    const headingSet = new Set(headings);
    const usedIds = new Set(
      Array.from(document.querySelectorAll('[id]'))
        .filter((element) => !headingSet.has(element))
        .map((element) => element.id)
        .filter(Boolean)
    );
    const fragment = document.createDocumentFragment();

    headings.forEach((heading, index) => {
      const text = heading.textContent.trim();
      const existingId = heading.getAttribute('id');
      const baseId = existingId || slugify(text) || `article-section-${index + 1}`;
      let headingId = baseId;
      let duplicateIndex = 2;

      while (usedIds.has(headingId)) {
        headingId = `${baseId}-${duplicateIndex}`;
        duplicateIndex += 1;
      }

      if (heading.id !== headingId) {
        heading.id = headingId;
      }

      usedIds.add(headingId);

      const item = document.createElement('li');
      item.className = `main-article__toc-item main-article__toc-item--${heading.tagName.toLowerCase()}`;

      const link = document.createElement('a');
      link.className = 'main-article__toc-link';
      link.href = `#${headingId}`;
      link.textContent = text;

      item.append(link);
      fragment.append(item);
    });

    tocList.replaceChildren(fragment);
    toc.hidden = false;
  };

  const setupSommaireClamp = (section) => {
    const toc = section.querySelector(tocSelector);
    const tocList = section.querySelector(tocListSelector);
    const content = section.querySelector(contentSelector);
    const toggle = section.querySelector('[data-main-article-toc-toggle]');

    if (!toc || !tocList || !content || !toggle) return;

    const desktop = window.matchMedia('(min-width: 990px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const readMoreLabel = toggle.textContent.trim() || 'Lire plus';
    const readLessLabel = toc.dataset.readLessLabel || 'Lire moins';

    // Only clamp when the sommaire is meaningfully taller than the text beside
    // it, and only when there is a real text column to clamp against.
    const OVERFLOW_THRESHOLD = 80;
    const MIN_CONTENT_HEIGHT = 80;
    // Never cap the sommaire below a usable total height (title + a couple of
    // entries + the toggle), even if the text column is very short.
    const MIN_CLAMP_HEIGHT = 200;

    let expanded = false;
    let frame = 0;

    const applyLabel = () => {
      toggle.textContent = expanded ? readLessLabel : readMoreLabel;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    // max-height clamps the content box, so subtract the sommaire's own
    // vertical padding/border (unless it is border-box) to make the *total*
    // rendered height match the text column height.
    const clampTo = (totalHeight) => {
      const style = window.getComputedStyle(toc);
      const total = Math.max(Math.round(totalHeight), MIN_CLAMP_HEIGHT);
      let maxHeight = total;

      if (style.boxSizing !== 'border-box') {
        const chrome =
          parseFloat(style.paddingTop) +
          parseFloat(style.paddingBottom) +
          parseFloat(style.borderTopWidth) +
          parseFloat(style.borderBottomWidth);
        maxHeight = Math.max(total - chrome, 0);
      }

      toc.style.setProperty('--toc-clamp-height', `${maxHeight}px`);
    };

    // The sommaire floats right and the article text wraps in the column to its
    // left, then continues full-width *below* the float. What matters is not the
    // article's total height but how far the text reaches in that left column:
    // if the text there stops above the sommaire's bottom, the sommaire sticks
    // out into empty space. Measure the bottom of the last text line that sits
    // beside the float, using per-line rects (works regardless of scroll or the
    // fact that paragraph boxes span the full width behind the float).
    const measureTextBesideFloat = (floatRect) => {
      const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null);
      const range = document.createRange();
      const leftEdge = floatRect.left;
      let maxBottom = floatRect.top;
      let node;

      while ((node = walker.nextNode())) {
        if (!node.nodeValue || !node.nodeValue.trim()) continue;
        range.selectNodeContents(node);
        const rects = range.getClientRects();

        for (let i = 0; i < rects.length; i += 1) {
          const rect = rects[i];
          if (rect.width === 0 || rect.height === 0) continue;
          // A line in the left column, within the float's vertical band.
          if (rect.right <= leftEdge + 4 && rect.top < floatRect.bottom && rect.bottom > floatRect.top) {
            if (rect.bottom > maxBottom) maxBottom = rect.bottom;
          }
        }
      }

      return maxBottom;
    };

    const measure = () => {
      frame = 0;

      // Mobile: sommaire is a full-width block above the content — never clamp.
      if (!desktop.matches || toc.hidden) {
        toc.classList.remove('is-clamped', 'is-expanded');
        toc.style.removeProperty('--toc-clamp-height');
        toggle.hidden = true;
        expanded = false;
        applyLabel();
        return;
      }

      // Measure against the sommaire's natural (unclamped) height.
      toc.classList.remove('is-clamped');
      toc.style.removeProperty('--toc-clamp-height');
      const floatRect = toc.getBoundingClientRect();
      const tocNatural = floatRect.height;
      const besideHeight = measureTextBesideFloat(floatRect) - floatRect.top;

      if (tocNatural > besideHeight + OVERFLOW_THRESHOLD && besideHeight >= MIN_CONTENT_HEIGHT) {
        toc.classList.add('is-clamped');
        toc.classList.toggle('is-expanded', expanded);
        toggle.hidden = false;
        clampTo(besideHeight);
      } else {
        toc.classList.remove('is-expanded');
        toggle.hidden = true;
        expanded = false;
      }

      applyLabel();
    };

    const requestMeasure = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    toggle.addEventListener('click', () => {
      expanded = !expanded;
      toc.classList.toggle('is-expanded', expanded);
      applyLabel();

      if (!expanded) {
        const top = toc.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({
          top: Math.max(top, 0),
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
      }
    });

    // Note: we intentionally do NOT observe the content box for resize — the
    // clamp reflows the floated text, which would feed back into the observer.
    window.addEventListener('resize', requestMeasure, { passive: true });
    desktop.addEventListener('change', requestMeasure);
    window.addEventListener('load', requestMeasure, { once: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(requestMeasure).catch(() => {});
    }

    measure();
  };

  const bindRelatedControls = (section) => {
    const relatedList = section.querySelector(relatedListSelector);
    const controls = section.querySelectorAll(relatedControlSelector);
    const progress = section.querySelector(relatedProgressSelector);
    const progressThumb = progress?.querySelector(relatedProgressThumbSelector);

    if (!relatedList) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let progressFrame = 0;

    const getScrollStep = () => {
      const firstCard = relatedList.firstElementChild;

      if (!firstCard) return relatedList.clientWidth * 0.86;

      const styles = window.getComputedStyle(relatedList);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;

      return firstCard.getBoundingClientRect().width + gap;
    };

    const updateProgress = () => {
      progressFrame = 0;

      if (!progress || !progressThumb) return;

      const maxScroll = relatedList.scrollWidth - relatedList.clientWidth;

      if (maxScroll <= 1) {
        if (relatedList.children.length > 1) {
          progress.hidden = false;
          if (progress.classList.contains('fwn-slider')) {
            window.FWNSliders?.setPosition(progress, 0, 0);
            return;
          }
          progressThumb.style.setProperty('--article-related-progress-width', '28%');
          progressThumb.style.setProperty('--article-related-progress-left', '0%');
        } else {
          progress.hidden = true;
        }

        return;
      }

      progress.hidden = false;

      if (progress.classList.contains('fwn-slider')) {
        window.FWNSliders?.setPosition(progress, relatedList.scrollLeft, maxScroll);
        return;
      }

      const thumbWidth = Math.max(18, (relatedList.clientWidth / relatedList.scrollWidth) * 100);
      const thumbLeft = (relatedList.scrollLeft / maxScroll) * (100 - thumbWidth);

      progressThumb.style.setProperty('--article-related-progress-width', `${thumbWidth}%`);
      progressThumb.style.setProperty('--article-related-progress-left', `${thumbLeft}%`);
    };

    const requestProgressUpdate = () => {
      if (progressFrame) return;

      progressFrame = window.requestAnimationFrame(updateProgress);
    };

    controls.forEach((control) => {
      control.addEventListener('click', () => {
        const direction = control.dataset.direction === 'previous' ? -1 : 1;
        const maxScroll = Math.max(0, relatedList.scrollWidth - relatedList.clientWidth);
        const currentScroll = relatedList.scrollLeft;
        const shouldLoop = maxScroll > 1;
        let targetScroll = currentScroll + getScrollStep() * direction;

        if (shouldLoop) {
          if (direction > 0 && (currentScroll >= maxScroll - 2 || targetScroll > maxScroll)) {
            targetScroll = 0;
          } else if (direction < 0 && (currentScroll <= 2 || targetScroll < 0)) {
            targetScroll = maxScroll;
          }
        }

        relatedList.scrollTo({
          left: Math.max(0, Math.min(maxScroll, targetScroll)),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      });
    });

    relatedList.addEventListener('scroll', requestProgressUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(requestProgressUpdate);
      observer.observe(relatedList);
    } else {
      window.addEventListener('resize', requestProgressUpdate, { passive: true });
    }

    updateProgress();
  };

  const initArticle = () => {
    document.querySelectorAll(rootSelector).forEach((section) => {
      buildToc(section);
      setupSommaireClamp(section);
      bindRelatedControls(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initArticle, { once: true });
  } else {
    initArticle();
  }
})();
